import { useEffect, useState } from 'react';
import CopilotResource from 'Resources/Copilot';
import { useSoftDeployAccess } from 'App/components/hooks';

const POLL_MS = 15_000;

// Module-level cache so multiple components sharing this hook share a single
// poll loop's result without each starting its own setInterval. Sidebar +
// future banner can both read this without doubling Redis pressure.
let cachedAppointmentId: number | null = null;
let cachedAt = 0;
let inflight: Promise<number | null> | null = null;
const subscribers = new Set<(v: number | null) => void>();
let intervalHandle: ReturnType<typeof setInterval> | null = null;

const fetchOnce = (): Promise<number | null> => {
	if (inflight) return inflight;
	inflight = CopilotResource.getActiveSession()
		.then(s => {
			cachedAppointmentId = s?.appointmentId ?? null;
			cachedAt = Date.now();
			subscribers.forEach(fn => fn(cachedAppointmentId));
			return cachedAppointmentId;
		})
		.catch(err => {
			console.error('useActiveCopilotSession: poll failed', err);
			cachedAppointmentId = null;
			subscribers.forEach(fn => fn(null));
			return null;
		})
		.finally(() => {
			inflight = null;
		});
	return inflight;
};

const ensureInterval = () => {
	if (intervalHandle || subscribers.size === 0) return;
	intervalHandle = setInterval(() => {
		if (subscribers.size === 0) return;
		fetchOnce();
	}, POLL_MS);
};

const tearDownIfIdle = () => {
	if (subscribers.size === 0 && intervalHandle) {
		clearInterval(intervalHandle);
		intervalHandle = null;
	}
};

/** Polls the copilot active-session endpoint and returns the appointmentId, or null. */
export const useActiveCopilotSession = (): number | null => {
	const enabled = useSoftDeployAccess('AI_NEGOTIATION_COPILOT');
	const [appointmentId, setAppointmentId] = useState<number | null>(
		enabled ? cachedAppointmentId : null
	);

	useEffect(() => {
		if (!enabled) return;
		subscribers.add(setAppointmentId);
		// Reuse a recent cache (< 5s) to avoid hammering on mount cascades.
		if (Date.now() - cachedAt > 5_000) {
			fetchOnce();
		} else {
			setAppointmentId(cachedAppointmentId);
		}
		ensureInterval();
		return () => {
			subscribers.delete(setAppointmentId);
			tearDownIfIdle();
		};
	}, [enabled]);

	return enabled ? appointmentId : null;
};

export default useActiveCopilotSession;
